import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { wsUrl, api } from "@/lib/api";
import { toast } from "sonner";

/** Play a soft two-note chime via Web Audio API. No audio asset required. */
function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = [
      { f: 880, t: now,        d: 0.18 }, // A5
      { f: 1318.5, t: now+0.12, d: 0.28 }, // E6
    ];
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(n.f, n.t);
      gain.gain.setValueAtTime(0.0001, n.t);
      gain.gain.exponentialRampToValueAtTime(0.15, n.t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, n.t + n.d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(n.t);
      osc.stop(n.t + n.d + 0.02);
    }
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch { /* silent */ }
}

/**
 * Opens a single site-wide presence WebSocket per authed session. When any
 * OTHER user newly arrives on the site, fires a neon toast and dispatches
 * `nw:online-count` so the TopBar chip can update live. Plays a chime for
 * followed users when the viewer has opted in via `user.arrival_sound`.
 */
export function useSitePresence() {
  const { user, token } = useAuth();
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const attemptsRef = useRef(0);
  const shouldRunRef = useRef(true);
  const followingRef = useRef(new Set());

  // Fetch the set of followed user IDs on mount, and any time the user follows/unfollows.
  useEffect(() => {
    if (!user || user === false || user.is_guest) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/me/following");
        if (!alive) return;
        followingRef.current = new Set(data?.ids || []);
      } catch {/* silent */}
    };
    load();
    const onChange = () => load();
    window.addEventListener("nw:follow-changed", onChange);
    return () => { alive = false; window.removeEventListener("nw:follow-changed", onChange); };
  }, [user]);

  useEffect(() => {
    if (!user || user === false || !token) return;
    shouldRunRef.current = true;
    const myId = user.id;

    const cleanupTimers = () => {
      if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    };

    const emitCount = (n) => {
      if (typeof n === "number") {
        window.dispatchEvent(new CustomEvent("nw:online-count", { detail: { count: n } }));
      }
    };

    const connect = () => {
      if (!shouldRunRef.current) return;
      const url = `${wsUrl("/ws/presence")}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0;
        pingRef.current = setInterval(() => {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
        }, 25000);
      };

      ws.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); } catch { return; }
        if (data.type === "hello") {
          emitCount(data.online_count);
          return;
        }
        if (data.type === "left") {
          emitCount(data.online_count);
          return;
        }
        if (data.type === "arrived" && data.user && data.user.id !== myId) {
          emitCount(data.online_count);
          const isFollowed = followingRef.current.has(data.user.id);
          if (isFollowed && user.arrival_sound) playChime();
          toast(`✦ @${data.user.username} just arrived`, {
            description: data.online_count ? `${data.online_count} online right now` : "New face on NITEWIRE",
            style: {
              background: "#050505",
              border: `2px solid ${isFollowed ? "#D4FF00" : "#FF3BFF"}`,
              color: "#F4F4F0",
              borderRadius: 0,
              fontFamily: "JetBrains Mono, monospace",
            },
            action: {
              label: "View",
              onClick: () => { window.location.href = `/u/${data.user.username}`; },
            },
          });
          window.dispatchEvent(new CustomEvent("nw:site-arrival", { detail: data }));
        }
      };

      ws.onclose = () => {
        cleanupTimers();
        if (!shouldRunRef.current) return;
        const delay = Math.min(30000, 1000 * Math.pow(2, attemptsRef.current++));
        reconnectRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => { /* handled by onclose */ };
    };

    connect();

    return () => {
      shouldRunRef.current = false;
      cleanupTimers();
      try { wsRef.current?.close(); } catch {}
      wsRef.current = null;
    };
  }, [user, token]);
}
